import { useForm } from 'react-hook-form';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Send, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useConsultingLeadForm,
  consultingLeadSchema,
  type ConsultingLeadFormData,
} from '@/hooks/useConsultingLeadForm';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export function ConsultingLeadForm() {
  const t = useT();
  const {
    submitLead,
    isSubmitting,
    isUploading,
    uploadProgress,
    isSuccess,
    setIsSuccess,
    attachments,
    setAttachments,
    interestOptions,
    companySizeOptions,
  } = useConsultingLeadForm();

  const isBusy = isSubmitting || isUploading;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ConsultingLeadFormData>({
    resolver: zodResolver(consultingLeadSchema),
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
      interest: '',
      company_size: '',
      message: '',
      privacy: false as unknown as true,
    },
  });

  const onSubmit = async (data: ConsultingLeadFormData) => {
    await submitLead(data);
  };

  const handleReset = () => {
    setIsSuccess(false);
    setAttachments([]);
    reset();
  };

  if (isSuccess) {
    return (
      <section className="py-20 md:py-28" id="consulting-form">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-2xl mx-auto text-center p-8 sm:p-12 rounded-2xl bg-secondary/30 border border-border/40"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              {t('Vielen Dank für Ihre Anfrage!', 'Thank you for your enquiry!')}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              {t(
                'Wir haben Ihre Consulting-Anfrage erhalten und melden uns innerhalb von 24 Stunden bei Ihnen. Lars Zimmermann wird sich persönlich mit Ihnen in Verbindung setzen.',
                'We have received your consulting request and will get back to you within 24 hours. Lars Zimmermann will contact you personally.',
              )}
            </p>
            <Button variant="outline" onClick={handleReset}>
              {t('Weitere Anfrage senden', 'Send another request')}
            </Button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28 bg-secondary/30" id="consulting-form">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center max-w-2xl mx-auto mb-12"
          {...fadeUp}
          transition={{ duration: 0.5 }}
        >
          <span className="badge-hero mb-4 inline-flex">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            {t('Unverbindlich anfragen', 'Non-binding enquiry')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mt-4">
            {t('Beratungsgespräch ', 'Request a ')}<span className="text-primary">{t('anfragen', 'consultation')}</span>
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            {t(
              'Erzählen Sie uns von Ihrem Vorhaben – wir melden uns innerhalb von 24 Stunden.',
              'Tell us about your project — we get back to you within 24 hours.',
            )}
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit(onSubmit)}
          className="max-w-2xl mx-auto space-y-6"
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Name + Company */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cl-name">
                {t('Name', 'Name')} <span className="text-primary">*</span>
              </Label>
              <Input
                id="cl-name"
                placeholder={t('Ihr vollständiger Name', 'Your full name')}
                aria-invalid={!!errors.name}
                {...register('name')}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl-company">{t('Firma', 'Company')}</Label>
              <Input
                id="cl-company"
                placeholder={t('Firmenname (optional)', 'Company name (optional)')}
                {...register('company')}
              />
            </div>
          </div>

          {/* Email + Phone */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cl-email">
                {t('E-Mail', 'Email')} <span className="text-primary">*</span>
              </Label>
              <Input
                id="cl-email"
                type="email"
                placeholder={t('ihre@email.de', 'your@email.com')}
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="cl-phone">{t('Telefon', 'Phone')}</Label>
              <Input
                id="cl-phone"
                type="tel"
                placeholder="+49 123 456789"
                {...register('phone')}
              />
            </div>
          </div>

          {/* Interest + Company Size */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                {t('Themenbereich', 'Topic')} <span className="text-primary">*</span>
              </Label>
              <Select onValueChange={(v) => setValue('interest', v, { shouldValidate: true })}>
                <SelectTrigger aria-invalid={!!errors.interest}>
                  <SelectValue placeholder={t('Bitte wählen...', 'Please select...')} />
                </SelectTrigger>
                <SelectContent>
                  {interestOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.interest && (
                <p className="text-sm text-destructive">{errors.interest.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t('Unternehmensgröße', 'Company size')}</Label>
              <Select onValueChange={(v) => setValue('company_size', v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('Optional wählen...', 'Optional...')} />
                </SelectTrigger>
                <SelectContent>
                  {companySizeOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="cl-message">{t('Ihre Nachricht', 'Your message')}</Label>
            <Textarea
              id="cl-message"
              placeholder={t('Beschreiben Sie kurz Ihr Anliegen oder Ihre Herausforderung...', 'Briefly describe your request or challenge...')}
              rows={5}
              {...register('message')}
            />
            {errors.message && (
              <p className="text-sm text-destructive">{errors.message.message}</p>
            )}
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>{t('Anhänge', 'Attachments')}</Label>
            <FileDropzone files={attachments} onFilesChange={setAttachments} disabled={isBusy} uploadProgress={uploadProgress} />
          </div>

          {/* DSGVO Checkbox */}
          <div className="space-y-2">
            <div className="flex items-start gap-3">
              <Checkbox
                id="cl-privacy"
                checked={!!watch('privacy')}
                onCheckedChange={(checked) =>
                  setValue('privacy', checked as true, { shouldValidate: true })
                }
                className={errors.privacy ? 'border-destructive' : ''}
              />
              <label htmlFor="cl-privacy" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                {t('Ich habe die ', 'I have read the ')}
                <LocalizedLink to="/datenschutz" className="text-primary hover:underline" target="_blank">
                  {t('Datenschutzerklärung', 'privacy policy')}
                </LocalizedLink>{' '}
                {t('und die ', 'and the ')}
                <LocalizedLink to="/agb" className="text-primary hover:underline" target="_blank">
                  {t('AGB', 'terms and conditions')}
                </LocalizedLink>{' '}
                {t('gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage zu. ', 'and consent to the processing of my data for handling my enquiry. ')}
                <span className="text-primary">*</span>
              </label>
            </div>
            {errors.privacy && (
              <p className="text-sm text-destructive flex items-center gap-1 ml-7">
                <AlertCircle className="w-3 h-3" />
                {errors.privacy.message}
              </p>
            )}
          </div>

          {/* Submit */}
          <Button
            type="submit"
            variant="hero"
            size="lg"
            className="w-full sm:w-auto"
            disabled={isBusy}
          >
            {isBusy ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isUploading
                  ? t('Dateien werden hochgeladen...', 'Uploading files...')
                  : t('Wird gesendet...', 'Sending...')}
              </>
            ) : (
              <>
                {t('Anfrage absenden', 'Send request')}
                <Send className="w-5 h-5" />
              </>
            )}
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
